// System
import { useSnackbar } from 'notistack';
import { AppDispatch } from 'src/app/store';
import { useFormState } from 'react-hook-form';
import { BaseSyntheticEvent, useMemo } from 'react';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import { useProviderDrawer } from './useProviderDrawer';
import {
  setShowUpdateProviderConfigForm,
  setShowCancelUpdateProviderConfigForm,
  setSelectedProviderConfig,
  setShowDeleteProviderConfigForm,
} from '@Modal/reducers/providers/updateProviderConfigSlice/updateProviderConfigSlice';

// Form
import { useCreateProviderConfigForm } from '@Forms/providers/CreateProviderConfigFormContext';

// Types
import { StoreInterface } from 'src/app/types';
// Graphql
import {
  useDeleteProviderConfigMutation,
  useGetProviderConfigsQuery,
  useUpdateProviderConfigMutation,
} from '@Src/generated.graphql';

export const useUpdateProviderConfig = () => {
  // Redux
  const dispatch = useDispatch<AppDispatch>();
  const { selectedProviderData } = useProviderDrawer();
  const [updateProviderConfig] = useUpdateProviderConfigMutation();
  const [deleteProviderConfig] = useDeleteProviderConfigMutation();
  const { refetch: refetchProviderConfigs } = useGetProviderConfigsQuery({
    variables: { id: selectedProviderData?.id },
  });

  const {
    showCancelUpdateProviderConfigForm,
    showUpdateProviderConfigForm,
    selectedProviderConfig,
    showDeleteProviderConfigForm,
  } = useSelector((state: StoreInterface) => state?.updateProviderConfig);

  // Other Hooks
  const { enqueueSnackbar } = useSnackbar();

  // Handle Form Cancels
  const handleUpdateCancel = () => {
    if (isDirty) {
      return dispatch(setShowCancelUpdateProviderConfigForm(true));
    } else {
      return dispatch(setShowUpdateProviderConfigForm(false));
    }
  };

  const handleCancelTakeMeBack = (action: string) => {
    if (action === 'back') {
      dispatch(setShowCancelUpdateProviderConfigForm(false));
      dispatch(setShowUpdateProviderConfigForm(true));
    } else {
      reset();
      dispatch(setShowCancelUpdateProviderConfigForm(false));
      dispatch(setShowUpdateProviderConfigForm(false));
    }
  };

  // Form Controls
  const { control, handleSubmit, reset } = useCreateProviderConfigForm();
  const { isDirty, errors } = useFormState({ control });

  const handleSubmitUpdateProviderConfig = useMemo(
    () => (e?: BaseSyntheticEvent) => {
      return handleSubmit(async (formData) => {
        const convertedBetSuggestions =
          typeof formData.betSuggestions === 'string'
            ? formData.betSuggestions.split(',').map(Number)
            : formData?.betSuggestions;
        const convertedFormData = {
          ...formData,
          betSuggestions: convertedBetSuggestions,
          providerId: selectedProviderData?.id,
          isRoundable: formData.isRoundable == 'true',
        };
        try {
          await updateProviderConfig({
            variables: { configId: selectedProviderConfig.id, input: convertedFormData },
          });

          enqueueSnackbar('Provider config was successfully updated', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          // Refetch ProviderConfigs
          await refetchProviderConfigs();

          reset();
          dispatch(setShowUpdateProviderConfigForm(false));
        } catch (error) {
          enqueueSnackbar(error.message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        }
      })(e);
    },
    [updateProviderConfig]
  );

  const handleDeleteProviderConfig = async () => {
    try {
      const configId = selectedProviderConfig?.id;
      await deleteProviderConfig({ variables: { configId } });

      enqueueSnackbar('Provider config was successfully deleted', {
        variant: 'success',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
      dispatch(setShowDeleteProviderConfigForm(false));
      // Refetch ProviderConfigs
      await refetchProviderConfigs();
    } catch (error) {
      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 2000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  return {
    // Store
    selectedProviderConfig,
    showUpdateProviderConfigForm,
    showDeleteProviderConfigForm,
    showCancelUpdateProviderConfigForm,
    submittable: isDirty && Object.values(errors).length == 0,
    // Actions
    setSelectedProviderConfig,
    setShowUpdateProviderConfigForm,
    setShowDeleteProviderConfigForm,
    setShowCancelUpdateProviderConfigForm,
    handleUpdateCancel,
    handleCancelTakeMeBack,
    handleDeleteProviderConfig,
    handleSubmitUpdateProviderConfig,
  };
};
